import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHead } from "@/pages/HistoryPage";

const AREAS = [
  { area: "brows", title: "Brows", body: "Microblading, nano strokes, powder/ombré, machine shading and combo brows — new or previously tattooed." },
  { area: "lips", title: "Lip Blush", body: "Lip blush, ombré lip or full lip color — virgin lips or lips that have been tattooed before." },
];

export default function NewConsultationPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState("");
  const auto = useRef(false);

  const start = async (area) => {
    setBusy(area);
    try {
      const { data } = await api.post("/consultations", { area });
      navigate(`/consult/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };

  useEffect(() => {
    const a = params.get("area");
    if (!auto.current && (a === "brows" || a === "lips")) {
      auto.current = true;
      start(a);
    }
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps -- run once per URL; `auto` ref guards re-entry

  return (
    <div className="mx-auto max-w-4xl px-6 py-10 sm:px-10 lg:py-14" data-testid="new-consultation-page">
      <PageHead eyebrow="New consultation" title="What are we working on today?" sub="Choose the procedure area. Ink Genius will then ask a few simple questions, one or two at a time." />
      <div className="mt-10 grid gap-4 sm:grid-cols-2">
        {AREAS.map(({ area, title, body }, i) => (
          <button key={area} onClick={() => start(area)} disabled={!!busy} data-testid={`new-consult-${area}-btn`}
            className="ig-rise group flex min-h-[180px] flex-col rounded-3xl border border-[#E6DBCF] bg-[#FDFBF7] p-7 text-left transition-colors duration-200 hover:border-[#2C1810] hover:bg-[#FDF2F0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C5A059] disabled:opacity-60"
            style={{ animationDelay: `${i * 0.08}s` }}>
            <span className="font-serif text-4xl font-semibold text-[#2C1810]">{title}</span>
            <span className="mt-3 flex-1 text-sm leading-relaxed text-[#4A3B32]">{body}</span>
            <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#9C6218]">
              {busy === area ? <Loader2 size={14} className="animate-spin" /> : null} Start <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
